import React, { useEffect, useState } from 'react'
import { useNls } from '../../../nls/nlsProvider'
import type {
  OpsBookingDue,
  OpsCustomer,
  OpsMembership,
  OpsMembershipLedgerEntry,
} from '../../../types/ops'
import type { Workspace } from '../../../types/workspace'
import { Button, StatusPill } from '../../Common'
import {
  subscribeBookingDuesByCustomer,
  subscribeMembershipLedger,
  subscribeMembershipsByCustomer,
} from '../../../services/opsBookingsService'

export interface MemberPlansAndDuesTabProps {
  workspace: Workspace
  customer: OpsCustomer
}

export function MemberPlansAndDuesTab({
  workspace,
  customer,
}: MemberPlansAndDuesTabProps): React.ReactElement {
  const { t } = useNls()

  const [memberships, setMemberships] = useState<OpsMembership[]>([])
  const [ledgerEntries, setLedgerEntries] = useState<OpsMembershipLedgerEntry[]>([])
  const [dues, setDues] = useState<OpsBookingDue[]>([])
  const [ledgerLimit, setLedgerLimit] = useState<number>(20)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  useEffect(() => {
    let unsubs: Array<() => void> = []
    if (workspace.id && customer.id) {
      const unsubM = subscribeMembershipsByCustomer(
        workspace.id,
        customer.id,
        (items) => setMemberships(items),
        (err) => setErrorMsg(err.message),
      )
      unsubs.push(unsubM)

      const unsubD = subscribeBookingDuesByCustomer(
        workspace.id,
        customer.id,
        (items) => setDues(items),
        (err) => setErrorMsg(err.message),
      )
      unsubs.push(unsubD)
    }

    return () => {
      unsubs.forEach((u) => u())
      unsubs = []
    }
  }, [workspace.id, customer.id])

  // Subscribe to ledger of active membership if available
  const activeMembership =
    memberships.find(
      (m) =>
        m.state === 'ACTIVE' || (m as unknown as { status?: string }).status === 'ACTIVE',
    ) || memberships[0]

  useEffect(() => {
    let unsub: (() => void) | undefined
    if (activeMembership?.id) {
      unsub = subscribeMembershipLedger(
        activeMembership.id,
        (items) => setLedgerEntries(items),
        (err) => setErrorMsg(err.message),
      )
    }
    return () => {
      if (unsub) unsub()
    }
  }, [activeMembership?.id])

  const visibleLedger = ledgerEntries.slice(0, ledgerLimit)
  const hasMoreLedger = ledgerEntries.length > ledgerLimit

  return (
    <div className="memberPlansAndDuesTab">
      {errorMsg && (
        <div className="memberLinkBookingsCard__error" role="alert">
          {errorMsg}
        </div>
      )}

      {/* Memberships Section */}
      <section className="memberPlansAndDuesTab__section" aria-label={t('NLSMemberBookingsMembershipsTitle')}>
        <h2 className="memberPlansAndDuesTab__sectionTitle">
          {t('NLSMemberBookingsMembershipsTitle')}
        </h2>

        {memberships.length === 0 ? (
          <p className="memberBookingsListTab__empty">
            {t('NLSMemberBookingsEmptyMemberships')}
          </p>
        ) : (
          memberships.map((m) => {
            const stateStr =
              m.state || (m as unknown as { status?: string }).status || ''
            let tone: 'success' | 'warning' | 'danger' | 'neutral' = 'neutral'
            if (stateStr === 'ACTIVE') tone = 'success'
            else if (stateStr === 'FROZEN') tone = 'warning'
            else if (stateStr === 'CANCELLED' || stateStr === 'EXPIRED') tone = 'danger'

            const planName =
              m.planSnapshot?.name ||
              (m as unknown as { planName?: string }).planName ||
              m.planId
            const totalSessions =
              m.planSnapshot?.sessionsTotal ??
              (m as unknown as { totalSessions?: number }).totalSessions
            const frozenFrom =
              m.freeze?.from ?? (m as unknown as { frozenFrom?: string }).frozenFrom
            const frozenTo =
              m.freeze?.to ?? (m as unknown as { frozenTo?: string }).frozenTo

            return (
              <div key={m.id} className="memberPlansAndDuesTab__card">
                <div className="memberBookingsListTab__itemHeader">
                  <h3 className="memberBookingsListTab__itemTitle">{planName}</h3>
                  <StatusPill tone={tone}>{stateStr}</StatusPill>
                </div>

                <div className="memberBookingsListTab__itemMeta">
                  {m.validTo && (
                    <span>
                      {t('NLSMemberBookingsValidTo', { date: m.validTo })}
                    </span>
                  )}
                  {totalSessions !== undefined && totalSessions !== null && (
                    <span>
                      {t('NLSMemberBookingsSessionsUsed', {
                        used: m.sessionsUsed || 0,
                        total: totalSessions,
                      })}
                    </span>
                  )}
                  {stateStr === 'FROZEN' && frozenFrom && (
                    <span>
                      {t('NLSMemberBookingsFrozenRange', {
                        from: frozenFrom,
                        to: frozenTo || 'TBD',
                      })}
                    </span>
                  )}
                </div>
              </div>
            )
          })
        )}
      </section>

      {/* Ledger Section */}
      {activeMembership && (
        <section className="memberPlansAndDuesTab__section" aria-label={t('NLSMemberBookingsLedgerTitle')}>
          <h2 className="memberPlansAndDuesTab__sectionTitle">
            {t('NLSMemberBookingsLedgerTitle')}
          </h2>

          {visibleLedger.length === 0 ? (
            <p className="memberBookingsListTab__empty">
              {t('NLSMemberBookingsEmptyUpcoming')}
            </p>
          ) : (
            <ul className="memberBookingsListTab__list">
              {visibleLedger.map((entry) => {
                const actionLabel =
                  (entry as unknown as { action?: string }).action || entry.type
                const diffVal =
                  (entry as unknown as { diff?: number }).diff ??
                  (entry.deltaUsed !== 0 ? entry.deltaUsed : entry.deltaHeld)
                return (
                  <li key={entry.id} className="memberBookingsListTab__item">
                    <div className="memberBookingsListTab__itemHeader">
                      <span className="memberBookingsListTab__itemTitle">{actionLabel}</span>
                      <StatusPill tone="neutral">{entry.reason}</StatusPill>
                    </div>
                    <div className="memberBookingsListTab__itemMeta">
                      <span>{new Date(entry.at).toLocaleString()}</span>
                      {diffVal !== undefined && (
                        <span>{diffVal > 0 ? `+${diffVal}` : `${diffVal}`}</span>
                      )}
                    </div>
                  </li>
                )
              })}
            </ul>
          )}

          {hasMoreLedger && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setLedgerLimit((prev) => prev + 20)}
            >
              {t('NLSMemberBookingsShowMore')}
            </Button>
          )}
        </section>
      )}

      {/* Dues Section */}
      <section className="memberPlansAndDuesTab__section" aria-label={t('NLSMemberBookingsDuesTitle')}>
        <div className="memberBookingsListTab__itemHeader">
          <h2 className="memberPlansAndDuesTab__sectionTitle">
            {t('NLSMemberBookingsDuesTitle')}
          </h2>
          <span className="memberPlansAndDuesTab__recordOnly">
            {t('NLSMemberBookingsPriceRecordOnly')}
          </span>
        </div>

        {dues.length === 0 ? (
          <p className="memberBookingsListTab__empty">
            {t('NLSMemberBookingsEmptyDues')}
          </p>
        ) : (
          dues.map((due) => {
            let tone: 'success' | 'warning' | 'danger' | 'neutral' = 'neutral'
            let stateLabel: string = due.state
            if (due.state === 'DUE') {
              tone = 'danger'
              stateLabel = t('NLSMemberBookingsDueStateDue')
            } else if (due.state === 'RECORDED_PAID') {
              tone = 'success'
              stateLabel = t('NLSMemberBookingsDueStatePaid')
            } else if (due.state === 'WAIVED') {
              tone = 'neutral'
              stateLabel = t('NLSMemberBookingsDueStateWaived')
            }

            const amountDisplay =
              (due as unknown as { amount?: number | string }).amount ??
              (due.amountMinor ? (due.amountMinor / 100).toFixed(2) : 0)
            const dueDateDisplay =
              (due as unknown as { dueDate?: string }).dueDate ?? due.dueKey

            return (
              <div key={due.id} className="memberPlansAndDuesTab__duesRow">
                <div className="memberBookingsListTab__itemMeta">
                  <strong>
                    {due.currency} {amountDisplay}
                  </strong>
                  {due.receiptRef && (
                    <span>
                      {t('NLSMemberBookingsReceiptRef', { ref: due.receiptRef })}
                    </span>
                  )}
                  {dueDateDisplay && <span>{dueDateDisplay}</span>}
                </div>
                <StatusPill tone={tone}>{stateLabel}</StatusPill>
              </div>
            )
          })
        )}
      </section>
    </div>
  )
}
